import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom"; // Renamed Link to RouterLink
import { AuthContext } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import LanguageSelector from "../components/LanguageSelector";

// Material UI components
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Paper,
  Fade,
  Link, // MUI Link
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

// Material UI icons
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import BusinessIcon from '@mui/icons-material/Business'; // Example icon for branding

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [registerError, setRegisterError] = useState(null);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { register, currentUser } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (currentUser) {
      navigate("/");
    }
  }, [currentUser, navigate]);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
    if (errors[name]) setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t("nameRequired") || "Name is required";
    if (!formData.email) newErrors.email = t("emailRequired") || "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t("pleaseEnterValidEmail") || "Email address is invalid";
    if (!formData.password) newErrors.password = t("passwordRequired") || "Password is required";
    else if (formData.password.length < 6) newErrors.password = t("passwordMinLength") || "Password must be at least 6 characters";
    if (!formData.confirmPassword) newErrors.confirmPassword = t("confirmPasswordRequired") || "Please confirm your password";
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = t("passwordsDoNotMatch") || "Passwords do not match";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setRegisterError(null);
    try {
      const { confirmPassword, ...userData } = formData;
      const result = await register(userData);
      if (result.success) {
        setRegisterSuccess(true);
        setTimeout(() => {
          navigate("/login", { state: { message: t("registrationSuccessful") || "Registration successful. Please login." } });
        }, 2000);
      } else {
        setRegisterError(result.message);
      }
    } catch (error) {
      setRegisterError(t("unexpectedError") || "An unexpected error occurred.");
      console.error("Registration error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Background decorative elements (optional, make them subtle) */}
      <Box
        sx={{
          position: "fixed",
          top: "-5%",
          left: "-5%",
          width: "clamp(300px, 35vw, 450px)", // Slightly different placement/size
          height: "clamp(300px, 35vw, 450px)",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0)} 70%)`,
          zIndex: -1,
        }}
      />
      <Box
        sx={{
          position: "fixed",
          bottom: "-10%",
          right: "-10%",
          width: "clamp(350px, 45vw, 550px)",
          height: "clamp(350px, 45vw, 550px)",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0)} 70%)`,
          zIndex: -1,
        }}
      />

      <LanguageSelector
        variant="icon"
        sx={{ position: "absolute", top: theme.spacing(2), right: theme.spacing(2), zIndex: 10 }}
      />

      <Container
        maxWidth="md"
        sx={{
          display: "flex",
          flexDirection: isSmall ? "column-reverse" : "row", // Form first on mobile
          alignItems: "center",
          justifyContent: "center",
          gap: isSmall ? 0 : theme.spacing(4),
          p: 0,
        }}
      >
        {/* Left side - Brand/Welcome area - Hidden on small screens, shown on medium+ */}
        {!isSmall && (
          <Fade in={true} timeout={1000}>
            <Box
              sx={{
                flex: "1 1 50%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                p: theme.spacing(3, 4),
                textAlign: "left",
                color: theme.palette.text.primary,
              }}
            >
              <img src="/assets/logo.png" alt="Logo" style={{ height: 80, marginBottom: theme.spacing(3) }} />
              <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: theme.palette.primary.main }}>
                {t("appName", "FleetPilot")}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300, maxWidth: 450 }}>
                {t("registerToJoinDescription") || "Join us to streamline your fleet management and operational reporting."}
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Right side - Register Form */}
        <Box
          sx={{
            flex: "1 1 auto",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            p: isSmall ? theme.spacing(2, 0) : 0,
          }}
        >
          <Fade in={true} timeout={800}>
            <Paper
              elevation={isMobile ? 0 : 8}
              sx={{
                width: "100%",
                maxWidth: 420,
                borderRadius: theme.shape.borderRadius * 0.5,
                overflow: "hidden",
              }}
            >
              <Box sx={{ p: { xs: 3, sm: 4 } }}>
                {isSmall && (
                  <Stack alignItems="center" sx={{ mb: 2 }}>
                    <img src="/assets/logo.png" alt="Logo" style={{ height: 80, marginBottom: theme.spacing(3) }} />
                    <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.primary.main }}>{t("appName", "FleetPilot")}</Typography>
                  </Stack>
                )}
                <Typography variant="h5" component="h1" fontWeight={700} sx={{ mb: 0.5, textAlign: isSmall ? 'center' : 'left' }}>
                  {t("createAccount") || "Create Your Account"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, textAlign: isSmall ? 'center' : 'left' }}>
                  {t("registerToJoin") || "Fill in the details below to register."}
                </Typography>

                {registerError && (
                  <Alert variant="outlined" severity="error" sx={{ mb: 2.5 }} icon={<ErrorOutlineIcon fontSize="inherit" />}>
                    {registerError}
                  </Alert>
                )}
                {registerSuccess && (
                  <Alert variant="outlined" severity="success" sx={{ mb: 2.5 }} icon={<CheckCircleOutlineIcon fontSize="inherit" />}>
                    {t("registrationSuccessfulShort") || "Success! Redirecting..."}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <TextField
                    fullWidth label={t("name") || "Full Name"} name="name" autoComplete="name" autoFocus
                    value={formData.name} onChange={handleChange} error={!!errors.name} helperText={errors.name}
                    disabled={isLoading || registerSuccess}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><PersonIcon color="action" /></InputAdornment>) }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth label={t("email") || "Email Address"} name="email" type="email" autoComplete="email"
                    value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email}
                    disabled={isLoading || registerSuccess}
                    InputProps={{ startAdornment: (<InputAdornment position="start"><EmailIcon color="action" /></InputAdornment>) }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth label={t("password") || "Password"} name="password" type={showPassword ? "text" : "password"} id="password"
                    autoComplete="new-password" value={formData.password} onChange={handleChange} error={!!errors.password} helperText={errors.password}
                    disabled={isLoading || registerSuccess}
                    InputProps={{
                      startAdornment: (<InputAdornment position="start"><LockIcon color="action" /></InputAdornment>),
                      endAdornment: (<InputAdornment position="end">
                        <IconButton onClick={togglePasswordVisibility} edge="end" size="small">{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton>
                      </InputAdornment>),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth label={t("confirmPassword") || "Confirm Password"} name="confirmPassword" type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword" autoComplete="new-password" value={formData.confirmPassword} onChange={handleChange}
                    error={!!errors.confirmPassword} helperText={errors.confirmPassword}
                    disabled={isLoading || registerSuccess}
                    InputProps={{
                      startAdornment: (<InputAdornment position="start"><LockIcon color="action" /></InputAdornment>),
                      endAdornment: (<InputAdornment position="end">
                        <IconButton onClick={toggleConfirmPasswordVisibility} edge="end" size="small">{showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton>
                      </InputAdornment>),
                    }}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2.5, textAlign: 'right' }}>
                    {t("passwordMinLength") || "Min. 6 characters"}
                  </Typography>
                  <Button
                    type="submit" fullWidth variant="contained" size="large"
                    startIcon={isLoading ? null : <AppRegistrationIcon />}
                    disabled={isLoading || registerSuccess}
                    sx={{ py: 1.25, fontWeight: 600 }}
                  >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : (t("register") || "Create Account")}
                  </Button>
                  <Divider sx={{ my: 3 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={400}>
                      {t("or") || "OR"}
                    </Typography>
                  </Divider>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {t("alreadyHaveAccount") || "Already have an account?"}{" "}
                    <Link component={RouterLink} to="/login" color="primary.main" sx={{ fontWeight: "bold" }}>
                      {t("login") || "Login"}
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Box>
      </Container>
      <Box sx={{ position: "absolute", bottom: theme.spacing(1), width: "100%", textAlign: "center", zIndex: 1 }}>
        <Typography variant="caption" color={isSmall ? "text.secondary" : alpha(theme.palette.text.primary, 0.7)} sx={{ opacity: 0.8 }}>
          &copy; {new Date().getFullYear()} {t("appName", "FleetPilot")}
        </Typography>
      </Box>
    </>
  );
};

export default Register;
